import { defineComponent, computed, createElementBlock, openBlock, Fragment, renderList, createVNode, withCtx, createTextVNode, toDisplayString, createElementVNode, reactive, ref, watch, normalizeClass, createBlock, createCommentVNode, defineAsyncComponent, onBeforeMount, mergeProps, withModifiers } from "vue";
import { registerEntityEndpoint, getEntityAPIClient, ROUTES, PLACEHOLDER_UUID, quasarValidateRules, useNotificationStore, CMSNotification } from "@editor/ui-utils";
import { QTooltip, QIcon, useFormChild, QToggle, QInput, QDialog, QCardSection, QCard, QBtn, copyToClipboard } from "quasar";
import { useI18n } from "vue-i18n";
import { getImage, EditorImage } from "@modules/image-editor-ui";
import { defineStore } from "pinia";
import { getPartner } from "@modules/partner-editor-ui";
const de = {
  videos: {
    contentBlockLabel: "Video",
    credit: "Videonachweis",
    aiGeneratedVideos: "Nur KI-generierte Videos anzeigen",
    partnerVideos: "Nur Videos externer Partner anzeigen",
    components: {
      videoSelectorHintInvalid: "ungültiges Video",
      videoSelectorHintUnknown: "unbekannter Fehler",
      uploadVideo: "Video hochladen",
      editVideo: "Video bearbeiten",
      videoSelect: "Auswählen",
      videoSelected: "Ausgewählt",
      videoExpiresIn: "Läuft in {n} Tagen ab",
      videoPublish: "Online in {n} Tagen",
      videoUnpublished: "Video nicht veröffentlicht",
      videoExpired: "Abgelaufen",
      videoPreviewImageExpired: "Vorschau abgelaufen",
      videoExpires: "Läuft ab am",
      videoCreated: "Live seit",
      videoTitle: "Titel",
      videoCredits: "Quelle",
      videoStatus: "Status",
      aspectText: "Vorschau für verschiedene Videogrößen basierend auf dem Fokuspunkt",
      aspectTeaser: "Teaser Home & Channel + Titelbild Mobile",
      aspectTitle: "Titelvideo auf Tablet & Desktop",
      aspectDesc: "Wenn der Fokuspunkt gesetzt wird, ist die Vorschau nach dem Speichern hier sichtbar.",
      aspectClick: "Bitte auf das quadratische Bild fokusieren",
      videoDeleted: "Gelöscht"
    },
    backButton: "Zurück zur Videoliste",
    title: "Titel",
    altText: "Alt-Text",
    altTextHint: "Wenn nicht befüllt wird der Titel genutzt",
    caption: "Bildunterschrift",
    selectVideo: "Video auswählen",
    addVideo: "Video hinzufügen",
    removeVideo: "Video entfernen",
    source: "Quelle",
    publishedAt: "Veröffentlichungsdatum",
    expiredAt: "Ablaufdatum",
    msn: "MSN Service",
    deleteVideo: "Video löschen",
    messageConfirmDelete1: "Video du dir sicher, dass du Bild",
    messageConfirmDelete2: "löschen möchtest?",
    messageMandatoryInformation: "Du kannst das Video nicht speichern, da benötigte Informationen fehlen",
    messageUpload: "Video hochgeladen",
    messageSaved: "Video gespeichert",
    messageDeleted: "Video gelöscht",
    invalid: "Ungültig",
    setFocalPoint: "Bitte setze den Fokuspunkt, indem du auf das Video klickst.*",
    articleLabel: "Artikel",
    invalidVideoCover: "Abgelaufen",
    invalidVideoCoverTooltip: "Die Videovorschau für dieses Video ist abgelaufen.",
    deletedVideoCover: "Gelöscht",
    deletedVideoCoverTooltip: "Die Videovorschau für dieses Video ist gelöscht.",
    notAnVideoFile: "Die ausgewählte Datei ist kein Video",
    license: "Lizenz",
    copyXymaticId: "Xymatic ID kopieren",
    copyVideoUuid: "Video UUID kopieren",
    transcript: {
      transcriptVideo: "Videotranskript erstellen",
      resultTitle: "Video-Transkript",
      formTitle: "Videotranskript erstellen",
      language: "Sprache",
      diarization: "Sprechererkennung aktivieren",
      minSpeakers: "Mindestanzahl der Sprecher",
      maxSpeakers: "Maximale Anzahl der Sprecher",
      createArticle: "Artikel erstellen",
      discard: "Transkript verwerfen",
      transcriptText: "Transkripttext",
      copyToClipboard: "Transkript in die Zwischenablage kopieren",
      transcriptInProgress: "Das Videotranskript wird erstellt. Bitte warten Sie, bis es fertig ist.",
      transcriptFailed: "Videotranskript fehlgeschlagen.",
      status: {
        NEW: "Neu",
        SUBMITTED_LOCALLY: "Lokal eingereicht",
        CONVERTING: "Wird konvertiert",
        CONVERSION_FAILED: "Konvertierung fehlgeschlagen",
        CONVERTED: "Konvertiert",
        UPLOADING_TO_CLOUD: "Wird in Cloud-Speicher hochgeladen",
        UPLOAD_FAILED: "Hochladen in Cloud-Speicher fehlgeschlagen",
        UPLOADED: "Hochgeladen",
        SUBMITTING_TO_PROVIDER: "Wird an Anbieter übermittelt",
        SUBMITTED_TO_PROVIDER: "An Anbieter übermittelt",
        SUBMITTING_FAILED: "Übermittlung an Anbieter fehlgeschlagen",
        WAITING_FOR_PROVIDER: "Warten auf Transkriptanbieter",
        TIMED_OUT: "Zeitüberschreitung",
        RUNNING: "Läuft",
        SUCCEEDED: "Erfolgreich",
        FAILED: "Fehlgeschlagen",
        TRANSCRIPT_FAILED: "Transkription fehlgeschlagen",
        UNKNOWN: "Unbekannt"
      }
    },
    createArticle: {
      formTitle: "Video Artikel erstellen",
      articleType: "Artikeltyp",
      newsArticle: "News Artikel",
      newsArticleTooltip: "Regulärer Artikel ohne Partnerlogo",
      partnerArticle: "Partner Artikel",
      partnerArticleTooltip: "Partner Artikel mit Partnerlogo",
      partnerArticleDisabledTooltip: "Aus diesem Video kann kein Partner Artikel erstellt werden, da es von keinem Partner stammt",
      articleBilling: "Artikel Abrechnung",
      articleIsNotBilled: "Eigener Textinhalt (nicht abgerechnet)",
      articleIsBilled: " Partner Textinhalt (abgerechnet)"
    }
  }
};
const en = {
  videos: {
    contentBlockLabel: "Video",
    credit: "Video credit",
    aiGeneratedVideos: "Show AI generated videos only",
    partnerVideos: "Show external partner videos only",
    components: {
      videoSelectorHintInvalid: "Invalid video",
      videoSelectorHintUnknown: "Unknown error",
      uploadVideo: "Upload Video",
      editVideo: "Edit Video",
      videoSelect: "Select video",
      videoSelected: "Selected",
      videoExpiresIn: "Expires in {n} days",
      videoExpired: "Expired",
      videoPreviewImageExpired: "Image preview expired",
      videoExpires: "Expires on",
      videoPublish: "Online in {n} days",
      videoUnpublished: "Video unpublished",
      videoCreated: "Live since",
      videoTitle: "Title",
      videoCredits: "Credits",
      videoStatus: "Status",
      aspectText: "Preview for different video sizes based on the focal point",
      aspectTeaser: "Teaser Home & Channel + Title Video Mobile",
      aspectTitle: "Title video on Tablet & Desktop",
      aspectDesc: "After setting the focal point, the preview is visible here after saving.",
      aspectClick: "Please focus on the square video",
      videoDeleted: "Deleted"
    },
    backButton: "Back to videos list",
    title: "Title",
    altText: "Alt Text",
    altTextHint: "If not filled, the title is used",
    caption: "Caption",
    selectVideo: "Select video",
    addVideo: "Add video",
    removeVideo: "Remove video",
    source: "Source",
    publishedAt: "Published at",
    expiredAt: "Expired at",
    msn: "MSN Service",
    deleteVideo: "Delete video",
    messageConfirmDelete1: "Are you sure you want to delete video",
    messageConfirmDelete2: "?",
    // eslint-disable-next-line @typescript-eslint/quotes
    messageMandatoryInformation: "You can't save this video because it is missing mandatory information",
    messageUpload: "Video uploaded",
    messageSaved: "Video saved",
    messageDeleted: "Video deleted",
    invalid: "Invalid",
    setFocalPoint: "Please set the focal point by clicking on the video.*",
    articleLabel: "Article",
    invalidVideoCover: "Expired",
    invalidVideoCoverTooltip: "This video has an expired video preview.",
    deletedVideoCover: "Deleted",
    deletedVideoCoverTooltip: "The video preview for this video has been deleted.",
    notAnVideoFile: "Selected file is not an video",
    license: "License",
    copyXymaticId: "Copy Xymatic ID",
    copyVideoUuid: "Copy Video UUID",
    transcript: {
      transcriptVideo: "Create Video Transcript",
      resultTitle: "Video Transcript",
      formTitle: "Create Video Transcript",
      language: "Language",
      diarization: "Enable speaker recognition",
      minSpeakers: "Minimum number of speakers",
      maxSpeakers: "Maximum number of speakers",
      createArticle: "Create article",
      discard: "Discard transcript",
      transcriptText: "Transcript text",
      copyToClipboard: "Copy transcript to clipboard",
      transcriptInProgress: "Video transcript is in progress. Please wait until it is finished.",
      transcriptFailed: "Video transcript failed.",
      status: {
        NEW: "New",
        SUBMITTED_LOCALLY: "Submitted Locally",
        CONVERTING: "Converting",
        CONVERSION_FAILED: "Conversion Failed",
        CONVERTED: "Converted",
        UPLOADING_TO_CLOUD: "Uploading to cloud storage",
        UPLOAD_FAILED: "Upload to cloud storage Failed",
        UPLOADED: "Uploaded",
        SUBMITTING_TO_PROVIDER: "Submitting to Provider",
        SUBMITTED_TO_PROVIDER: "Submitted to provider",
        SUBMITTING_FAILED: "Submitting to provider failed",
        WAITING_FOR_PROVIDER: "Waiting for Transcript Provider",
        TIMED_OUT: "Timed Out",
        RUNNING: "Running",
        SUCCEEDED: "Succeeded",
        FAILED: "Failed",
        TRANSCRIPT_FAILED: "Transcript Failed",
        UNKNOWN: "Unknown"
      }
    },
    createArticle: {
      formTitle: "Create Video Article",
      articleType: "Article type",
      newsArticle: "News article",
      newsArticleTooltip: "Regular news article without partner logo",
      partnerArticle: "Partner article",
      partnerArticleTooltip: "Partner article with partner logo",
      partnerArticleDisabledTooltip: "Partner article cannot be created from this video as it does not come from any known partner",
      articleBilling: "Article billing",
      articleIsNotBilled: "Own text (dont bill partner text)",
      articleIsBilled: " Partner text (bill text)"
    }
  }
};
const messages = { en, de };
const VideoModelName = "Videos";
const VideoService = registerEntityEndpoint(VideoModelName);
const videoClient = getEntityAPIClient(VideoModelName);
const { getEntity, listEntities } = VideoService;
const getVideos = async (search = null) => {
  return listEntities({ params: { search: search || null } });
};
const getVideo = async (id, includePreview = false) => {
  return getEntity(id, { params: { includePreview } });
};
const getVideoUrl = (uuid, width = 640, height = 360) => {
  return `${videoClient.defaults.baseURL}/${uuid}/url/${width}/${height}`;
};
const postVideoTranscript = async (videoTranscriptRequest) => {
  const { data } = await videoClient.post("transcript", videoTranscriptRequest);
  return data;
};
const getVideoTranscript = async (transcriptUuid) => {
  const { data } = await videoClient.get(`transcript/${transcriptUuid}`);
  return data;
};
const getDefaultVideoBlock = () => ({
  type: "VIDEO",
  videoUuid: "",
  videoTeaser: ""
});
const routes = [
  {
    path: `/${ROUTES.videos.path}`,
    meta: { i18RouteTitleKey: ROUTES.videos.path },
    children: [
      {
        path: "",
        name: "video_list",
        component: () => import("./video-list-CzSDhO2b.js"),
        meta: { requiredRight: "navigate_list_videos" }
      }
    ]
  }
];
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  if (!getRandomValues) {
    getRandomValues = typeof crypto !== "undefined" && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);
    if (!getRandomValues) {
      throw new Error("crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported");
    }
  }
  return getRandomValues(rnds8);
}
const byteToHex = [];
for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 256).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + "-" + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + "-" + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + "-" + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + "-" + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}
const randomUUID = typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID.bind(crypto);
const native = {
  randomUUID
};
function v4(options, buf, offset) {
  if (native.randomUUID && true && !options) {
    return native.randomUUID();
  }
  options = options || {};
  const rnds = options.random || (options.rng || rng)();
  rnds[6] = rnds[6] & 15 | 64;
  rnds[8] = rnds[8] & 63 | 128;
  return unsafeStringify(rnds);
}
const daysUntil = (videoDate) => {
  const scheduledIn = videoDate ? new Date(videoDate).getTime() : 1;
  return Math.ceil((scheduledIn - (/* @__PURE__ */ new Date()).getTime()) / (1e3 * 60 * 60 * 24));
};
function getArticleEditorData(video, articleType, isNotBilled) {
  var _a;
  const publisherUuid = (_a = video == null ? void 0 : video.userMetadata) == null ? void 0 : _a.publisherId;
  return {
    mainMediaRef: {
      mediaType: "video",
      autoplay: false,
      videoUuid: video._id
    },
    title: video.title,
    leadText: video.summary,
    articleAuthorshipRef: articleType === "news_article" && publisherUuid ? { type: "news_article", authorUuids: [] } : {
      type: "partner_article",
      isBought: true,
      isNotBilled: false,
      partnerUuid: publisherUuid || "",
      assemblerUuid: "00000000" + v4().slice(8)
    }
  };
}
const _sfc_main$3 = /* @__PURE__ */ defineComponent({
  __name: "video-status-label",
  props: {
    video: { type: Object, required: true },
    posterImageState: { type: String, required: true }
  },
  setup(__props, { expose: __expose }) {
    __expose();
    const props = __props;
    const { t } = useI18n();
    const icons = computed(() => {
      const statusIcons = [];
      if (props.video.state === "unpublish") {
        statusIcons.push({
          key: "unpublished",
          name: "alert-circle",
          color: "red",
          label: t("videos.components.videoUnpublished")
        });
        return statusIcons;
      }
      const daysUntilPublishStart = props.video.publishStartDate ? daysUntil(props.video.publishStartDate) : null;
      const daysUntilPublishEnd = props.video.publishEndDate ? daysUntil(props.video.publishEndDate) : null;
      if (daysUntilPublishStart !== null && daysUntilPublishStart <= 31 && daysUntilPublishStart > 0) {
        statusIcons.push({
          key: "online",
          name: "clock",
          color: "teal-9",
          label: t("videos.components.videoPublish", { n: daysUntilPublishStart })
        });
      }
      if (daysUntilPublishEnd !== null && daysUntilPublishEnd <= 31 && daysUntilPublishEnd > 0) {
        statusIcons.push({
          key: "expires",
          name: "clock",
          color: "red",
          label: t("videos.components.videoExpiresIn", { n: daysUntilPublishEnd })
        });
      }
      if (props.posterImageState !== "publish") {
        statusIcons.push({
          key: "expiredImage",
          name: "alert-circle",
          color: "teal-9",
          label: t("videos.components.videoPreviewImageExpired")
        });
      }
      return statusIcons;
    });
    const __returned__ = { props, t, icons, get QIcon() {
      return QIcon;
    }, get QTooltip() {
      return QTooltip;
    } };
    Object.defineProperty(__returned__, "__isScriptSetup", { enumerable: false, value: true });
    return __returned__;
  }
});
const _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};
const _hoisted_1$3 = { class: "flex justify-start status-icons" };
const _hoisted_2$2 = { key: 0 };
const _hoisted_3$2 = {
  key: 1,
  class: "flex align-center overlay-padding"
};
const _hoisted_4$1 = {
  "data-qa-video-status-label-unpublished": "",
  class: "text-xsmall text-weight-medium q-mr-xs"
};
const _hoisted_5$1 = ["data-qa-video-status-label"];
function _sfc_render$3(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", _hoisted_1$3, [
    (openBlock(true), createElementBlock(
      Fragment,
      null,
      renderList($setup.icons, (icon) => {
        return openBlock(), createElementBlock("div", {
          key: icon.key,
          class: "icon-wrapper"
        }, [
          $setup.icons.length > 1 ? (openBlock(), createElementBlock("div", _hoisted_2$2, [
            createVNode($setup["QIcon"], {
              "data-qa-video-square-status": icon.key,
              name: icon.name,
              color: icon.color,
              size: "16px"
            }, {
              default: withCtx(() => [
                createVNode(
                  $setup["QTooltip"],
                  {
                    anchor: "top middle",
                    self: "top middle",
                    offset: [10, 30],
                    class: "bg-grey-10 text-white shadow-4"
                  },
                  {
                    default: withCtx(() => [
                      createTextVNode(
                        toDisplayString(icon.label),
                        1
                        /* TEXT */
                      )
                    ]),
                    _: 2
                    /* DYNAMIC */
                  },
                  1024
                  /* DYNAMIC_SLOTS */
                )
              ]),
              _: 2
              /* DYNAMIC */
            }, 1032, ["data-qa-video-square-status", "name", "color"])
          ])) : (openBlock(), createElementBlock("div", _hoisted_3$2, [
            icon.key === "unpublished" ? (openBlock(), createElementBlock(
              Fragment,
              { key: 0 },
              [
                createElementVNode(
                  "span",
                  _hoisted_4$1,
                  toDisplayString(icon.label),
                  1
                  /* TEXT */
                ),
                createVNode($setup["QIcon"], {
                  "data-qa-video-single-status": icon.key,
                  name: icon.name,
                  color: icon.color,
                  size: "16px"
                }, null, 8, ["data-qa-video-single-status", "name", "color"])
              ],
              64
              /* STABLE_FRAGMENT */
            )) : (openBlock(), createElementBlock(
              Fragment,
              { key: 1 },
              [
                createVNode($setup["QIcon"], {
                  "data-qa-video-single-status": icon.key,
                  name: icon.name,
                  color: icon.color,
                  size: "16px",
                  class: "q-mr-xs"
                }, null, 8, ["data-qa-video-single-status", "name", "color"]),
                createElementVNode("span", {
                  "data-qa-video-status-label": icon.key,
                  class: "text-xsmall text-weight-medium"
                }, toDisplayString(icon.label), 9, _hoisted_5$1)
              ],
              64
              /* STABLE_FRAGMENT */
            ))
          ]))
        ]);
      }),
      128
      /* KEYED_FRAGMENT */
    ))
  ]);
}
const VideoStatusLabel = /* @__PURE__ */ _export_sfc(_sfc_main$3, [["render", _sfc_render$3], ["__scopeId", "data-v-273e5e0a"], ["__file", "/builds/CT/content-technology-framework/modules/video/editor-ui/src/components/video-status-label.vue"]]);
const store = reactive({
  currentVideo: void 0
});
const useVideoStore = () => store;
const _sfc_main$2 = /* @__PURE__ */ defineComponent({
  __name: "editor-video",
  props: {
    square: { type: Boolean, required: false, default: false },
    layout: { type: String, required: false, default: "video" },
    entity: { type: String, required: false, default: "" },
    videoData: { type: Object, required: true }
  },
  setup(__props, { expose: __expose }) {
    var _a, _b, _c;
    __expose();
    const props = __props;
    const videoStore = useVideoStore();
    const isActive = ref(false);
    const video = ref();
    const posterImageState = ref("publish");
    const selectPlay = () => {
      var _a2, _b2;
      isActive.value = true;
      videoStore.currentVideo = ((_a2 = props.videoData) == null ? void 0 : _a2._id) || "";
      (_b2 = video.value) == null ? void 0 : _b2.play();
    };
    const getPosterImageState = async () => {
      var _a2;
      if (((_a2 = props.videoData) == null ? void 0 : _a2.posterImageUuid) && props.videoData.posterImageUuid !== PLACEHOLDER_UUID) {
        const posterImage = await getImage(props.videoData.posterImageUuid);
        posterImageState.value = posterImage.image.state;
      }
    };
    watch(videoStore, () => {
      var _a2;
      if (videoStore.currentVideo != props.videoData._id) {
        (_a2 = video.value) == null ? void 0 : _a2.pause();
        isActive.value = false;
      }
    });
    watch(
      () => props.videoData.posterImageUuid,
      async () => {
        if (props.videoData.posterImageUuid) {
          await getPosterImageState();
        } else {
          posterImageState.value = "publish";
        }
      },
      { immediate: true }
    );
    const selectPreview = (event) => {
      var _a2;
      const element = event.currentTarget;
      const video2 = (_a2 = element == null ? void 0 : element.parentNode) == null ? void 0 : _a2.querySelector(".video");
      isActive.value = !isActive.value;
      video2 == null ? void 0 : video2.pause();
    };
    const videoSource = ((_c = (_b = (_a = props.videoData) == null ? void 0 : _a.videoRenditions) == null ? void 0 : _b[0]) == null ? void 0 : _c.src) || "";
    const hover = ref(false);
    const __returned__ = { props, videoStore, isActive, video, posterImageState, selectPlay, getPosterImageState, selectPreview, videoSource, hover, get EditorImage() {
      return EditorImage;
    }, get QIcon() {
      return QIcon;
    }, VideoStatusLabel };
    Object.defineProperty(__returned__, "__isScriptSetup", { enumerable: false, value: true });
    return __returned__;
  }
});
const _hoisted_1$2 = {
  "data-qa-video-preview": "",
  class: "video-preview"
};
const _hoisted_2$1 = {
  ref: "video",
  "data-qa-video": "",
  class: "video",
  width: "212",
  height: "120",
  controls: ""
};
const _hoisted_3$1 = ["src"];
function _sfc_render$2(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock(
    "div",
    {
      class: normalizeClass([$props.layout !== "video" ? $props.layout : "", "video-wrapper"]),
      onMouseenter: _cache[0] || (_cache[0] = ($event) => $setup.hover = true),
      onMouseleave: _cache[1] || (_cache[1] = ($event) => $setup.hover = false)
    },
    [
      createElementVNode(
        "div",
        {
          class: normalizeClass({ videoActive: $setup.isActive })
        },
        [
          createElementVNode("div", {
            class: "play-btn-close",
            onClick: $setup.selectPreview
          }, [
            createVNode($setup["QIcon"], {
              name: "close",
              size: "24px",
              class: "btn-close q-mr-sm",
              color: "white"
            })
          ]),
          createElementVNode("div", _hoisted_1$2, [
            createElementVNode("div", {
              class: "play-btn",
              onClick: $setup.selectPlay
            }),
            createVNode($setup["EditorImage"], {
              uuid: $setup.props.videoData.posterImageUuid ?? "",
              size: "smallVideo",
              entity: $props.entity,
              square: $props.square
            }, null, 8, ["uuid", "entity", "square"]),
            $setup.video ? (openBlock(), createBlock($setup["VideoStatusLabel"], {
              key: 0,
              video: $setup.props.videoData,
              "poster-image-state": $setup.posterImageState
            }, null, 8, ["video", "poster-image-state"])) : createCommentVNode("v-if", true)
          ]),
          createElementVNode(
            "video",
            _hoisted_2$1,
            [
              createElementVNode("source", {
                src: $setup.videoSource,
                type: "video/mp4"
              }, null, 8, _hoisted_3$1)
            ],
            512
            /* NEED_PATCH */
          )
        ],
        2
        /* CLASS */
      )
    ],
    34
    /* CLASS, NEED_HYDRATION */
  );
}
const EditorVideo = /* @__PURE__ */ _export_sfc(_sfc_main$2, [["render", _sfc_render$2], ["__scopeId", "data-v-1825e9e6"], ["__file", "/builds/CT/content-technology-framework/modules/video/editor-ui/src/components/editor-video.vue"]]);
const _sfc_main$1 = {};
const _hoisted_1$1 = { class: "flex flex-center video-placeholder" };
function _sfc_render$1(_ctx, _cache) {
  return openBlock(), createElementBlock("div", _hoisted_1$1);
}
const EditorVideoPlaceholder = /* @__PURE__ */ _export_sfc(_sfc_main$1, [["render", _sfc_render$1], ["__scopeId", "data-v-11785185"], ["__file", "/builds/CT/content-technology-framework/modules/video/editor-ui/src/components/editor-video-placeholder.vue"]]);
const videoEntity = "video";
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "video-list.selector",
  props: {
    isMain: { type: Boolean, required: true, default: false },
    autoplay: { type: Boolean, required: false, default: void 0 },
    isSpotted: { type: Boolean, required: false, default: false },
    label: { type: String, required: false, default: void 0 },
    videoUuid: { type: String, required: true, default: "" },
    disable: { type: Boolean, required: false, default: false },
    required: { type: Boolean, required: false, default: false },
    square: { type: Boolean, required: false, default: false },
    rules: { type: Array, required: false, default: void 0 },
    license: { type: null, required: false, default: void 0 },
    status: { type: String, required: false, default: void 0 }
  },
  emits: ["update:video", "update:videoUuid", "update:autoplay", "update:isSpotted"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const VideoList = defineAsyncComponent(() => import("./video-list-CzSDhO2b.js"));
    const props = __props;
    const emit = __emit;
    const validationMsg = ref("");
    const isValid = ref(true);
    const hasAutoplay = computed({
      get: () => props.autoplay || false,
      set: (value) => {
        emit("update:autoplay", value);
      }
    });
    const videoIsSpotted = computed({
      get: () => props.isSpotted || false,
      set: (value) => {
        emit("update:isSpotted", value);
        emit("update:autoplay", !value);
      }
    });
    const VideoData = ref({
      _id: void 0,
      title: "",
      videoRenditions: [],
      posterRenditions: [],
      posterImageUuid: "",
      state: void 0,
      publishStartDate: void 0,
      publishEndDate: void 0,
      license: [],
      legacy: {
        source: "vibe",
        isEditableInCTF: false,
        isRenderedByCTF: false,
        hasBeenModifiedInCTF: false
      }
    });
    const dialogVisible = ref(false);
    const clickedNewVideo = ref(false);
    const wrapper = ref();
    __expose({ VideoData });
    const validateRules = () => {
      const validationRulesResult = quasarValidateRules(VideoData.value, props.rules);
      isValid.value = !validationRulesResult;
      validationMsg.value = validationRulesResult;
    };
    useFormChild({
      validate() {
        var _a;
        validateRules();
        if (!isValid.value) {
          (_a = wrapper.value) == null ? void 0 : _a.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        }
        return isValid.value;
      },
      resetValidation() {
        isValid.value = true;
      }
    });
    const showDialog = () => {
      if (!props.disable) {
        dialogVisible.value = true;
      }
    };
    const videoSelected = (video) => {
      clickedNewVideo.value = true;
      VideoData.value = video;
      emit("update:video", video);
      emit("update:videoUuid", VideoData.value._id);
      emit("update:autoplay", true);
      emit("update:isSpotted", false);
      dialogVisible.value = false;
    };
    const { t } = useI18n();
    const tLabel = computed(() => !props.label ? t("videos.selectVideo") : t(props.label));
    const clearVideoData = () => {
      VideoData.value = {
        _id: void 0,
        title: "",
        videoRenditions: []
      };
      validateRules();
      emit("update:video", void 0);
      emit("update:videoUuid");
      emit("update:autoplay", void 0);
      emit("update:isSpotted", void 0);
      clickedNewVideo.value = false;
      dialogVisible.value = false;
    };
    onBeforeMount(async () => {
      if (props.videoUuid) {
        VideoData.value = await getVideo(props.videoUuid);
      }
    });
    watch(
      () => props.videoUuid,
      async () => {
        if (props.videoUuid) {
          VideoData.value = await getVideo(props.videoUuid);
          validateRules();
        } else {
          VideoData.value = { _id: void 0, title: "" };
        }
      },
      { deep: true, immediate: true }
    );
    const __returned__ = { VideoList, props, emit, validationMsg, isValid, hasAutoplay, videoIsSpotted, videoEntity, VideoData, dialogVisible, clickedNewVideo, wrapper, validateRules, showDialog, videoSelected, t, tLabel, clearVideoData, get PLACEHOLDER_UUID() {
      return PLACEHOLDER_UUID;
    }, get QBtn() {
      return QBtn;
    }, get QCard() {
      return QCard;
    }, get QCardSection() {
      return QCardSection;
    }, get QDialog() {
      return QDialog;
    }, get QIcon() {
      return QIcon;
    }, get QInput() {
      return QInput;
    }, get QToggle() {
      return QToggle;
    }, get QTooltip() {
      return QTooltip;
    }, EditorVideo, EditorVideoPlaceholder };
    Object.defineProperty(__returned__, "__isScriptSetup", { enumerable: false, value: true });
    return __returned__;
  }
});
const _hoisted_1 = { ref: "wrapper" };
const _hoisted_2 = { class: "row justify-center items-center align-center text-center" };
const _hoisted_3 = { class: "col-6" };
const _hoisted_4 = {
  key: 0,
  class: "required"
};
const _hoisted_5 = { class: "video-container items-center" };
const _hoisted_6 = { class: "video-description" };
const _hoisted_7 = {
  key: 0,
  class: "text-primary"
};
const _hoisted_8 = {
  key: 1,
  "data-qa-article-video-title": ""
};
const _hoisted_9 = {
  key: 0,
  class: "video-toggle-controls"
};
const _hoisted_10 = { class: "spotted-container" };
const _hoisted_11 = { class: "spotted-child" };
const _hoisted_12 = { class: "spotted-child" };
const _hoisted_13 = { class: "tooltip-text" };
const _hoisted_14 = { key: 0 };
const _hoisted_15 = { class: "q-field__messages text-negative col" };
const _hoisted_16 = ["textContent"];
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock(
    "div",
    _hoisted_1,
    [
      createVNode($setup["QCard"], {
        flat: "",
        class: normalizeClass({ invalid: !$setup.isValid })
      }, {
        default: withCtx(() => [
          !$props.videoUuid ? (openBlock(), createBlock($setup["QCardSection"], {
            key: 0,
            style: { "height": "100%" },
            "data-qa-video-picker": "",
            class: "custom-video-wrapper border-item border-item--dashed q-mt-md",
            onClick: $setup.showDialog
          }, {
            default: withCtx(() => [
              createElementVNode("div", _hoisted_2, [
                createElementVNode("div", _hoisted_3, [
                  !$setup.VideoData.title ? (openBlock(), createBlock($setup["QIcon"], {
                    key: 0,
                    size: "36px",
                    name: "video",
                    color: "grey-5"
                  })) : createCommentVNode("v-if", true),
                  createVNode($setup["QInput"], mergeProps({
                    modelValue: $setup.VideoData.title,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => $setup.VideoData.title = $event),
                    "data-qa-video": "",
                    class: "hidden",
                    "append-icon": "external-link",
                    clearable: "",
                    readonly: "",
                    borderless: ""
                  }, _ctx.$attrs, {
                    "onClick:clear": $setup.videoSelected,
                    "onClick:append": $setup.showDialog
                  }), null, 16, ["modelValue"]),
                  !$setup.VideoData.title ? (openBlock(), createBlock($setup["QCardSection"], {
                    key: 1,
                    flat: "",
                    class: "q-pa-sm",
                    color: "white"
                  }, {
                    default: withCtx(() => [
                      createTextVNode(
                        toDisplayString($setup.tLabel),
                        1
                        /* TEXT */
                      ),
                      $props.required ? (openBlock(), createElementBlock("span", _hoisted_4)) : createCommentVNode("v-if", true)
                    ]),
                    _: 1
                    /* STABLE */
                  })) : createCommentVNode("v-if", true)
                ])
              ])
            ]),
            _: 1
            /* STABLE */
          })) : (openBlock(), createBlock($setup["QCardSection"], {
            key: 1,
            style: { "height": "100%" },
            class: "q-pa-none border-item"
          }, {
            default: withCtx(() => [
              createElementVNode("div", _hoisted_5, [
                $setup.VideoData._id !== $setup.PLACEHOLDER_UUID ? (openBlock(), createBlock($setup["EditorVideo"], {
                  key: 0,
                  "video-data": $setup.VideoData,
                  entity: $setup.videoEntity,
                  square: $props.square,
                  size: "smallVideo",
                  onClick: _cache[1] || (_cache[1] = withModifiers(() => {
                  }, ["stop"]))
                }, null, 8, ["video-data", "square"])) : (openBlock(), createBlock($setup["EditorVideoPlaceholder"], {
                  key: 1,
                  class: "video-placeholder"
                })),
                createElementVNode("div", _hoisted_6, [
                  createElementVNode("div", null, [
                    $setup.VideoData.shortTitle ? (openBlock(), createElementBlock(
                      "div",
                      _hoisted_7,
                      toDisplayString($setup.VideoData.shortTitle),
                      1
                      /* TEXT */
                    )) : createCommentVNode("v-if", true),
                    $setup.VideoData.title ? (openBlock(), createElementBlock(
                      "p",
                      _hoisted_8,
                      toDisplayString($setup.VideoData.title),
                      1
                      /* TEXT */
                    )) : createCommentVNode("v-if", true),
                    createVNode($setup["QBtn"], {
                      "data-qa-video-picker-remove-button": "",
                      outline: "",
                      color: "white",
                      "text-color": "black",
                      label: "Remove",
                      class: "border-btn",
                      disable: $props.disable,
                      onClick: $setup.clearVideoData
                    }, null, 8, ["disable"])
                  ]),
                  $props.isMain ? (openBlock(), createElementBlock("div", _hoisted_9, [
                    createElementVNode("div", _hoisted_10, [
                      createElementVNode("div", _hoisted_11, [
                        createVNode($setup["QToggle"], {
                          modelValue: $setup.videoIsSpotted,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => $setup.videoIsSpotted = $event),
                          "data-qa-video-picker-is-spotted-toggle": "",
                          label: $setup.t("articlesEdit.videoSpotted"),
                          disable: $props.disable
                        }, null, 8, ["modelValue", "label", "disable"])
                      ]),
                      createElementVNode("div", _hoisted_12, [
                        createVNode($setup["QIcon"], {
                          name: "info",
                          class: "q-mr-sm icon",
                          color: "grey"
                        }, {
                          default: withCtx(() => [
                            createVNode($setup["QTooltip"], {
                              "max-width": "350px",
                              class: "bg-black text-body2",
                              anchor: "top middle",
                              self: "top middle",
                              offset: [10, 40]
                            }, {
                              default: withCtx(() => [
                                createElementVNode(
                                  "div",
                                  _hoisted_13,
                                  toDisplayString($setup.t("articlesEdit.videoSpottedTooltip")),
                                  1
                                  /* TEXT */
                                )
                              ]),
                              _: 1
                              /* STABLE */
                            })
                          ]),
                          _: 1
                          /* STABLE */
                        })
                      ])
                    ]),
                    !$setup.videoIsSpotted ? (openBlock(), createElementBlock("div", _hoisted_14, [
                      createVNode($setup["QToggle"], {
                        modelValue: $setup.hasAutoplay,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => $setup.hasAutoplay = $event),
                        "data-qa-video-picker-autoplay-toggle": "",
                        label: $setup.t("articlesEdit.videoAutoplay"),
                        disable: $props.disable
                      }, null, 8, ["modelValue", "label", "disable"])
                    ])) : createCommentVNode("v-if", true)
                  ])) : createCommentVNode("v-if", true)
                ])
              ])
            ]),
            _: 1
            /* STABLE */
          })),
          createCommentVNode(" Custom quasar-like validation "),
          $setup.validationMsg ? (openBlock(), createBlock($setup["QCardSection"], {
            key: 2,
            class: "q-field__bottom row items-start q-field__bottom--animated"
          }, {
            default: withCtx(() => [
              createElementVNode("div", _hoisted_15, [
                createElementVNode("div", {
                  role: "alert",
                  textContent: toDisplayString($setup.validationMsg)
                }, null, 8, _hoisted_16)
              ])
            ]),
            _: 1
            /* STABLE */
          })) : createCommentVNode("v-if", true)
        ]),
        _: 1
        /* STABLE */
      }, 8, ["class"]),
      createVNode($setup["QDialog"], {
        modelValue: $setup.dialogVisible,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => $setup.dialogVisible = $event),
        "data-qa-video-modal": "",
        "full-width": "",
        "full-height": "",
        onHide: _cache[7] || (_cache[7] = ($event) => $setup.dialogVisible = false)
      }, {
        default: withCtx(() => [
          !$setup.clickedNewVideo ? (openBlock(), createBlock($setup["VideoList"], {
            key: 0,
            dialog: true,
            license: $props.license,
            status: $props.status,
            "onNew:clicked": _cache[4] || (_cache[4] = ($event) => $setup.clickedNewVideo = true),
            onClose: $setup.clearVideoData,
            "onClick:video": $setup.videoSelected,
            "onBack:modal": _cache[5] || (_cache[5] = ($event) => $setup.dialogVisible = false)
          }, null, 8, ["license", "status"])) : createCommentVNode("v-if", true)
        ]),
        _: 1
        /* STABLE */
      }, 8, ["modelValue"])
    ],
    512
    /* NEED_PATCH */
  );
}
const VideoListSelector = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render], ["__scopeId", "data-v-77cda947"], ["__file", "/builds/CT/content-technology-framework/modules/video/editor-ui/src/selectors/video-list.selector.vue"]]);
const transcriptStatusMap = /* @__PURE__ */ new Map([
  ["New", "NEW"],
  ["Submitted Locally", "SUBMITTED_LOCALLY"],
  ["Converting", "CONVERTING"],
  ["Conversion Failed", "CONVERSION_FAILED"],
  ["Converted", "CONVERTED"],
  ["Uploading to cloud storage", "UPLOADING_TO_CLOUD"],
  ["Upload to cloud storage Failed", "UPLOAD_FAILED"],
  ["Uploaded", "UPLOADED"],
  ["Submitting to Provider", "SUBMITTING_TO_PROVIDER"],
  ["Submitted to provider", "SUBMITTED_TO_PROVIDER"],
  ["Submitting to provider failed", "SUBMITTING_FAILED"],
  ["Waiting for Transcript Provider", "WAITING_FOR_PROVIDER"],
  ["Timed Out", "TIMED_OUT"],
  ["Running", "RUNNING"],
  ["Succeeded", "SUCCEEDED"],
  ["Failed", "FAILED"],
  ["Transcript Failed", "TRANSCRIPT_FAILED"],
  ["Unknown", "UNKNOWN"]
]);
const getStatusTagColor = (statusCode) => {
  if (["FAILED", "CONVERSION_FAILED", "UPLOAD_FAILED", "TRANSCRIPT_FAILED", "SUBMITTING_FAILED", "TIMED_OUT"].includes(statusCode))
    return "negative";
  if (["SUCCEEDED"].includes(statusCode))
    return "positive";
  return "info";
};
const getTranscriptStatusCode = (statusFormatted) => {
  return transcriptStatusMap.get(statusFormatted) || "UNKNOWN";
};
const isTranscriptFinished = (statusCode) => ["SUCCEEDED", "FAILED", "CONVERSION_FAILED", "UPLOAD_FAILED", "TRANSCRIPT_FAILED", "SUBMITTING_FAILED", "TIMED_OUT"].includes(statusCode);
function getArticleContentBlock(transcript) {
  const block = {
    type: "TEXT",
    content: ""
  };
  const { phrases } = transcript;
  if (!(phrases == null ? void 0 : phrases.length) && !phrases)
    return block;
  for (const phrase of phrases) {
    block.content += `<p>
    ${phrase.speaker ? "[speaker" + phrase.speaker + "]<br />" : ""}
    ${phrase.text}
    </p>`;
  }
  return block;
}
async function getArticleTranscriptEditorData(transcript, video) {
  var _a;
  return {
    mainMediaRef: {
      mediaType: "video",
      autoplay: false,
      videoUuid: video._id
    },
    title: video.title,
    leadText: video.summary,
    contentsRef: [getArticleContentBlock(transcript)],
    articleAuthorshipRef: await getArticleAuthorship(((_a = video == null ? void 0 : video.userMetadata) == null ? void 0 : _a.publisherId) || "")
  };
}
async function getArticleAuthorship(publisherId) {
  const baseAuthorship = {
    type: "news_article",
    authorUuids: []
  };
  if (!publisherId)
    return baseAuthorship;
  const publisher = await getPartner(publisherId);
  const dummyAssemblerUuid = "00000000" + v4().slice(8);
  if (publisher.partnerType === "agenturen")
    return {
      type: "agency_article",
      assemblerUuid: dummyAssemblerUuid,
      partnerUuid: publisher._id,
      authorUuids: []
    };
  else if (["testpartner", "contentpartner", "spezialpartner"].includes(publisher.partnerType))
    return {
      type: "partner_article",
      assemblerUuid: dummyAssemblerUuid,
      partnerUuid: publisher._id,
      isBought: true,
      isNotBilled: true
    };
  else
    return baseAuthorship;
}
const REQUEST_TIMEOUT_MS = 2 * 60 * 1e3;
const REFRESH_INTERVAL_MS = 3 * 1e3;
const useVideoTranscriptStore = defineStore("videoTranscript", () => {
  const id = ref();
  const status = ref();
  const video = ref();
  const transcript = ref();
  const error = ref();
  const refreshInterval = ref();
  const refreshTimeout = ref();
  const { showNotification } = useNotificationStore();
  const { t } = useI18n();
  const isFinished = computed(() => isTranscriptFinished(status.value || "UKNOWN"));
  const isFinishedSuccesfully = computed(() => status.value === "SUCCEEDED");
  async function initVideoTranscript(videoTranscriptRequest) {
    try {
      if (id.value)
        clearTranscriptData();
      const transcriptRes = await postVideoTranscript(videoTranscriptRequest);
      if (transcriptRes) {
        id.value = transcriptRes.id;
        status.value = getTranscriptStatusCode(transcriptRes.status);
        video.value = videoTranscriptRequest.video;
        transcript.value = transcriptRes;
      }
      refreshInterval.value = setInterval(refreshVideoTranscript, REFRESH_INTERVAL_MS);
      refreshTimeout.value = setTimeout(() => clearVideoTranscriptInterval("TIMED_OUT"), REQUEST_TIMEOUT_MS);
    } catch (e) {
      showNotification(
        new CMSNotification(t("videos.transcript.transcriptFailed"), "negative", {
          multiLine: true,
          timeout: null,
          dismissible: true,
          callbackLabel: t("messages.copyErrorToClipboard"),
          async callbackFunc() {
            await copyToClipboard(JSON.stringify(e, void 0, 2));
            showNotification(new CMSNotification(t("messages.copiedToClipboard"), "info"));
          }
        })
      );
    }
  }
  async function refreshVideoTranscript() {
    if (!id.value) {
      clearVideoTranscriptInterval();
      return;
    }
    try {
      const transcriptRes = await getVideoTranscript(id.value);
      status.value = getTranscriptStatusCode(transcriptRes.status);
      transcript.value = transcriptRes;
      if (transcript.value.error_details)
        error.value = transcript.value.error_details;
      if (isTranscriptFinished(status.value)) {
        clearVideoTranscriptInterval();
      }
    } catch (e) {
      clearVideoTranscriptInterval("TRANSCRIPT_FAILED", e instanceof Error ? e.message : void 0);
    }
  }
  function clearVideoTranscriptInterval(statusToSet, errorToSet) {
    if (refreshInterval.value)
      clearInterval(refreshInterval.value);
    refreshInterval.value = void 0;
    if (!isFinished.value && statusToSet)
      status.value = statusToSet;
    if (errorToSet)
      error.value = errorToSet;
    clearVideoTranscriptTimeout();
  }
  function clearVideoTranscriptTimeout() {
    if (refreshTimeout.value)
      clearTimeout(refreshTimeout.value);
    refreshTimeout.value = void 0;
  }
  function clearTranscriptData() {
    clearVideoTranscriptInterval();
    id.value = void 0;
    status.value = void 0;
    error.value = void 0;
    video.value = void 0;
    transcript.value = void 0;
  }
  return {
    id,
    status,
    video,
    transcript,
    refreshInterval,
    refreshTimeout,
    isFinished,
    isFinishedSuccesfully,
    initVideoTranscript,
    refreshVideoTranscript,
    clearVideoTranscriptInterval,
    clearVideoTranscriptTimeout,
    clearTranscriptData
  };
});
const VideoContentBlock = defineAsyncComponent(() => import("./video-content-block-iI43bYMw.js"));
const videoModule = {
  messages,
  routes,
  contentBlocks: [
    { type: "VIDEO", label: "videos.contentBlockLabel", icon: "video", component: VideoContentBlock, defaultValue: getDefaultVideoBlock() }
  ]
};
export {
  EditorVideo as E,
  VideoListSelector as V,
  _export_sfc as _,
  getArticleTranscriptEditorData as a,
  getStatusTagColor as b,
  VideoService as c,
  daysUntil as d,
  VideoContentBlock as e,
  VideoModelName as f,
  getArticleEditorData as g,
  getVideos as h,
  getVideo as i,
  getVideoUrl as j,
  getVideoTranscript as k,
  getDefaultVideoBlock as l,
  postVideoTranscript as p,
  useVideoTranscriptStore as u,
  videoModule as v
};
//# sourceMappingURL=index-B-PLdGUK.js.map
